import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Church, MapPin, Search, Users } from "lucide-react"
import ChurchConnectCard from "./ChurchConnectCard"
import { StatusMessage } from "../ui/StatusMessage"
import { getApiBaseUrl } from "../../config/apiBase"
import "../../styles/components/ChurchOnboarding.css"

const buildQuery = (filters) => {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value.trim()) {
      params.set(key, value.trim())
    }
  })
  return params.toString()
}

export default function FindChurchPage() {
  const [filters, setFilters] = useState({ query: "", denomination: "", city: "", state: "" })
  const [churches, setChurches] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const apiUrl = getApiBaseUrl()
  const queryString = useMemo(() => buildQuery(filters), [filters])

  useEffect(() => {
    const controller = new AbortController()
    const loadChurches = async () => {
      setLoading(true)
      setError("")
      try {
        const response = await fetch(`${apiUrl}/api/ChurchInvites/churches${queryString ? `?${queryString}` : ""}`, {
          signal: controller.signal,
        })
        if (!response.ok) {
          throw new Error("Could not load churches right now.")
        }
        setChurches(await response.json())
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Could not load churches.")
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    loadChurches()
    return () => controller.abort()
  }, [apiUrl, queryString])

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }))
  }

  return (
    <main className="church-onboarding-page">
      <section className="church-finder-hero">
        <span className="church-onboarding-kicker">
          <Church size={16} aria-hidden="true" />
          Find a church
        </span>
        <h1>Find a church to walk with</h1>
        <p>
          You can use TYK without being connected to a church, but you can also search for a church body,
          connect when membership is open, or request membership when a church has a covenant process.
        </p>
      </section>

      <section className="church-finder-search" aria-label="Church search">
        <label>
          Search
          <div>
            <Search size={16} aria-hidden="true" />
            <input
              value={filters.query}
              onChange={(event) => updateFilter("query", event.target.value)}
              placeholder="Church name, mission, or keyword"
            />
          </div>
        </label>
        <label>
          Tradition
          <input
            value={filters.denomination}
            onChange={(event) => updateFilter("denomination", event.target.value)}
            placeholder="Baptist, Catholic, Orthodox..."
          />
        </label>
        <label>
          City
          <input value={filters.city} onChange={(event) => updateFilter("city", event.target.value)} />
        </label>
        <label>
          State
          <input value={filters.state} onChange={(event) => updateFilter("state", event.target.value)} />
        </label>
      </section>

      {loading && <StatusMessage tone="loading" title="Searching churches" message="Finding church profiles in TYK." />}
      {error && <StatusMessage tone="error" title="Could not search churches" message={error} />}

      {!loading && !error && (
        <section className="church-finder-results">
          {churches.length === 0 ? (
            <StatusMessage
              tone="empty"
              title="No church profiles found"
              message="Try widening your search. Unaffiliated users can still use TYK and connect to a church later."
            />
          ) : (
            churches.map((churchRecord) => (
              <article key={churchRecord.groupId} className="church-result-card">
                <div className="church-result-main">
                  <span className="church-onboarding-kicker">
                    <Users size={15} aria-hidden="true" />
                    {churchRecord.membershipMode === "Application" ? "Membership request" : "Open connection"}
                  </span>
                  <h2>{churchRecord.groupName}</h2>
                  <p>
                    {[churchRecord.city, churchRecord.state, churchRecord.country].filter(Boolean).join(", ") || "Location not listed"}
                  </p>
                  {churchRecord.denominationOrTradition && <strong>{churchRecord.denominationOrTradition}</strong>}
                  <div className="church-result-stats">
                    <span><Users size={15} aria-hidden="true" /> {churchRecord.enrolledMembers || 0} enrolled in TYK</span>
                    {churchRecord.memberCountEstimate && <span><MapPin size={15} aria-hidden="true" /> {churchRecord.memberCountEstimate} estimated members</span>}
                  </div>
                  <Link to={`/group/${churchRecord.groupId}`}>View profile</Link>
                </div>
                <ChurchConnectCard churchRecord={churchRecord} compact />
              </article>
            ))
          )}
        </section>
      )}
    </main>
  )
}
